import { cva, type VariantProps } from "class-variance-authority";
import { LucideIcon } from "lucide-react";
import React from "react";
import { Link } from "react-router";
import { cn } from "src/common/utils";


const iconButtonVariants = cva(
  "rounded-md text-white transition-colors duration-200",
  {
    variants: {
      variant: {
        red: "bg-red-600 data-[clickable=true]:hover:bg-red-600/80",
        green: "bg-green-600 data-[clickable=true]:hover:bg-green-600/80",
        orange: "bg-orange data-[clickable=true]:hover:bg-orange/80",
        slate: "bg-accent data-[clickable=true]:hover:bg-cyan",
        blue: "bg-blue-600 data-[clickable=true]:hover:bg-blue-600/80",
        amber: "bg-amber-500 data-[clickable=true]:hover:bg-amber-500/80",
        none: "",
      },
      size: {
        sm: "p-0.5",
        md: "p-1",
        lg: "p-2",
      },
    },
    defaultVariants: {
      variant: "slate",
      size: "md",
    },
  }
);

export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants> {
  icon: LucideIcon;
  onClick?: () => void;
  href?: string;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size, icon: Icon, onClick, href, ...props }, ref) => {
    const isClickable = !!(onClick || href);
    
    if (href) {
      // For links, filter out button-specific props
      const buttonSpecificProps = ["form", "formAction", "formEncType", "formMethod", "formNoValidate", "formTarget", "type", "value"];
      const linkProps = Object.fromEntries(
        Object.entries(props).filter(([key]) => !buttonSpecificProps.includes(key))
      );
      return (
        <Link
          to={href}
          className={cn(iconButtonVariants({ variant, size, className }))}
          data-clickable={true}
          {...linkProps}
        >
          <Icon size={size === "sm" ? 16 : 20} />
        </Link>
      );
    }
    
    const Component = onClick ? "button" : "div";
    
    return (
      <Component
        className={cn(iconButtonVariants({ variant, size, className }))}
        onClick={onClick}
        data-clickable={isClickable}
        // @ts-ignore
        ref={onClick ? (ref as React.Ref<HTMLButtonElement>) : (ref as React.Ref<HTMLDivElement>)}
        {...props}
      >
        <Icon size={size === "sm" ? 16 : 20} />
      </Component>
    );
  }
);
IconButton.displayName = "IconButton";

export { iconButtonVariants }
